import '../icons'
import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { fromEvent, takeUntil } from 'rxjs'

@customElement('schmancy-option')
export default class SchmancyOption extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
				cursor: pointer;
				user-select: none;
				outline: none;
				/* The row inside takes this same corner; the host carries it too
				   because an outline follows its own element's radius, and a square
				   focus ring around a curved row overhangs at all four corners. */
				border-radius: var(--schmancy-radius-lg, 0.625rem);
			}

			:host(:focus-visible) {
				outline: 2px solid var(--schmancy-sys-color-primary-default);
				outline-offset: -2px;
			}

			:host([hidden]) {
				display: none;
			}

			:host([disabled]) {
				opacity: 0.5;
				pointer-events: none;
			}
		`,
	]
	@property({ type: String })
	value: string = ''

	@property({ type: String })
	label: string = ''

	@property({ type: Boolean, reflect: true })
	selected: boolean = false

	@property({ type: Boolean, reflect: true })
	disabled: boolean = false

	@property({ type: String })
	group: string = ''

	@property({ type: String })
	icon: string = ''

	/** The row a keyboard stands on while focus stays in a field beside the list. */
	@property({ type: Boolean, reflect: true })
	active: boolean = false

	/** A second, smaller line under the label. */
	@property({ type: String })
	secondary: string = ''

	connectedCallback() {
		super.connectedCallback()

		if (!this.id) {
			this.id = `schmancy-option-${Math.random().toString(36).substring(2, 9)}`
		}

		if (!this.label) {
			this.label = this.textContent?.trim() || this.value
		}

		if (!this.hasAttribute('value') && !this.value && this.textContent) {
			this.value = this.textContent.trim()
		}

		// A real ARIA option: exposed to the accessibility tree (and CDP) as a
		// direct listbox child, and focusable via a roving tabindex (a consumer
		// like schmancy-select or schmancy-place-picker can move focus to it) —
		// without joining the normal Tab order.
		if (!this.hasAttribute('tabindex')) this.tabIndex = -1
		this.setAttribute('role', 'option')
		this.reflectAriaState()

		fromEvent<MouseEvent>(this, 'click')
			.pipe(takeUntil(this.disconnecting))
			.subscribe(e => {
				e.stopPropagation()
				if (this.disabled) return
				this.dispatchEvent(
					new CustomEvent('option-select', {
						bubbles: true,
						composed: true,
						detail: { value: this.value },
					}),
				)
			})

		fromEvent<KeyboardEvent>(this, 'keydown')
			.pipe(takeUntil(this.disconnecting))
			.subscribe(e => {
				if (e.key === ' ' || e.key === 'Enter') {
					e.preventDefault()
					e.stopPropagation()
					if (this.disabled) return
					this.dispatchEvent(
						new CustomEvent('option-select', {
							bubbles: true,
							composed: true,
							detail: { value: this.value },
						}),
					)
				}
			})
	}

	disconnectedCallback() {
		super.disconnectedCallback()
	}

	protected override updated(changed: PropertyValues) {
		super.updated(changed)
		if (changed.has('selected') || changed.has('disabled') || changed.has('active')) this.reflectAriaState()
	}

	private reflectAriaState() {
		this.setAttribute('aria-selected', String(this.selected || this.active))
		if (this.disabled) this.setAttribute('aria-disabled', 'true')
		else this.removeAttribute('aria-disabled')
	}

	render() {
		const classes = {
			'py-2': true,
			'px-3': true,
			'rounded-lg': true,
			'text-sm': true,
			'w-full': true,
			flex: true,
			'items-center': true,
			'gap-2': true,
			'bg-primary-container': this.selected,
			'text-primary-onContainer': this.selected,
			'bg-secondary-container': this.active && !this.selected,
			'text-secondary-onContainer': this.active && !this.selected,
			'hover:bg-surface-high': !this.selected && !this.active,
			'focus:outline-none': true,
		}

		return html`
			<div class=${this.classMap(classes)}>
				${this.icon ? html`<schmancy-icon class="shrink-0 text-xl" aria-hidden="true">${this.icon}</schmancy-icon>` : ''}
				<span class="grid min-w-0 flex-1">
					<span>${this.label || this.value}</span>
					${this.secondary ? html`<span class="text-xs text-surface-onVariant">${this.secondary}</span>` : ''}
				</span>
				${
					this.selected
						? html`
								<span class="check">
									<svg
										width="16"
										height="16"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										stroke-width="2"
										stroke-linecap="round"
										stroke-linejoin="round"
									>
										<polyline points="20 6 9 17 4 12"></polyline>
									</svg>
								</span>
							`
						: ''
				}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-option': SchmancyOption
	}
}
